'use client';

import React from 'react';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import {
  IContactInformation,
  IDescriptionsResort,
  IFirstHandReport,
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Hero400Error } from '@/components/400';
import { useSanitizedHtmlProps } from '@/util/sanitized';
import { ResortPhotosData } from '@/components/widget/types';
import { INearbyResort } from '@/api/region/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { ResortGeneralInfo } from '@/components/resort/ResortGeneralInfo';
import { OverviewTopSlider } from '@/components/resort/OverviewTopSlider';
import { ImagesSection } from '@/components/resort/ImagesSection';
import { HowToGetThereSection } from '@/components/resort/HowToGetThereSection';
import { NearbyResorts } from '@/components/resort/NearbyResorts';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import RelatedArticles from '@/components/resort/RelatedArticles';
import ElfsightInstagramWidget from '@/components/resort/ElfsightInstagramWidget/Index';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { PageBody } from '@/components/common/PageBody';

const filterOutTabData = (data?: {
  dangerouslySetInnerHTML: {
    __html: string;
  };
}) => {
  if (!data) {
    return data;
  }
  const FORBIDDEN_CHARACTERS = '►';
  const result = data;

  // eslint-disable-next-line no-underscore-dangle
  let html = data.dangerouslySetInnerHTML.__html;
  const forbiddenCharacters = FORBIDDEN_CHARACTERS.split('');
  forbiddenCharacters.forEach((char) => {
    html = html.replace(new RegExp((char), 'g'), '');
  });
  // eslint-disable-next-line no-underscore-dangle
  result.dangerouslySetInnerHTML.__html = html;
  return result;
};

export type SummerOverviewPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  resortStrengths: IResortStrengthsOverview,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  snowfallInfoAnnual: Array<ISnowfallItem> | null,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
  trailmap: IResortTrailmapPage,
  contactInformation: IContactInformation,
  reviewsGeneral: IReviewsGeneral | null
  relatedNews: IRelatedNews | null,
  initials: {
    firstHand: IFirstHandReport | null,
  },
  photos: ResortPhotosData,
  nearbyResorts: INearbyResort[],
  resortReviews: IResortReviews
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'summer-overview-page';
export const TEMPLATE_ID = '3.2.10';
export const TEMPLATE_NAME = 'Resort Summer Overview';

export default function SummerOverviewPage({
  fullResort,
  pathInfo,
  resortStrengths,
  resortDescriptionTabs,
  trailmap,
  contactInformation,
  photos,
  nearbyResorts,
  snowfallInfoAnnual,
  snowfallInfoMonthly,
  relatedNews,
  resort,
}: SummerOverviewPageProps) {
  const topSliderPhotos = [...photos];

  const tabs = (resortDescriptionTabs.reduce((prev: Record<string, string>, curr) => ({ ...prev, [curr.tab]: (curr.description || '') }), {}));

  const resortDescription = useSanitizedHtmlProps(tabs.resort_summer ?? '');

  const transportationDescription = useSanitizedHtmlProps(tabs.transportation_profile ?? '');

  const { city } = contactInformation.address;
  const { country } = contactInformation.address;

  const totalSnowfall = snowfallInfoAnnual?.reduce((prev, curr) => prev + curr.totalSnow, 0);

  const annualSnowfall = totalSnowfall && snowfallInfoAnnual && Math.ceil(totalSnowfall / snowfallInfoAnnual?.length);

  const { t } = useOTSTranslation('common');

  const instagram = resort.socialMediaAccounts.find((acc) => acc.type === 'instagram');

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const isSummerMode = true;

  return (
    <Main>
      <div className="relative overflow-clip xl:mt-4">

        <div className="flex flex-col-reverse max-md:mt-[39px] md:flex-row-reverse">
          {trailmap && resortStrengths
            && fullResort.forecast && (
              <div className="flex min-h-0 w-full flex-col items-stretch md:w-[360px] md:shrink-0">
                {Boolean(!!instagram && fullResort?.isPartner) && (
                  <div className="pb-[19px] pl-[19px]">
                    <ElfsightInstagramWidget uuid={fullResort.uuid} />
                  </div>
                )}
                <ResortGeneralInfo
                  trailmap={trailmap}
                  resortStrengths={resortStrengths}
                  snowfallInfoMonthly={snowfallInfoMonthly}
                  snowfallInfoAnnual={snowfallInfoAnnual}
                  pathInfo={pathInfo}
                  annualSnowfall={annualSnowfall ?? undefined}
                  isSummerMode={isSummerMode}
                />
                <div>
                  <div>
                    <RelatedArticles
                      relatedNewsList={relatedNews}
                      isSummerMode={isSummerMode}
                    />
                  </div>
                  <SidebarAd id={pathInfo.uuid} isSummerMode={isSummerMode} />
                </div>
              </div>
          )}
          <section className="min-w-0 flex-1">
            <PageBody>
              {photos && resortDescription && (
                <OverviewTopSlider
                  photos={topSliderPhotos}
                  resortDescription={filterOutTabData(resortDescription)}
                  title={t('resort.summerOverview.summerTab')}
                  isSummerMode={isSummerMode}
                />
              )}

              <RegularAd isFirst />

              {photos && photos.length > 2 && (
                <ImagesSection photos={photos.slice(1)} />
              )}

              {transportationDescription && contactInformation && (
                <HowToGetThereSection
                  city={city}
                  contactInformation={contactInformation}
                  country={country}
                  description={filterOutTabData(transportationDescription)}
                  fullResort={fullResort}
                  isSummerMode={isSummerMode}
                  adSlot={<RegularAd />}
                />
              )}

              {nearbyResorts
                && <NearbyResorts nearbyResorts={nearbyResorts.slice(1)} isSummerMode={isSummerMode} />}
            </PageBody>
          </section>

        </div>
      </div>

    </Main>
  );
}

// fetchProps moved to ./summer-overview.server.ts so the App Router RSC page
// can import the data fetcher without pulling in this 'use client' bundle.
